import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Snackbar,
  LinearProgress,
  Avatar,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Search,
  Person,
  School,
  Download,
  Print,
  Share,
  ExpandMore,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  CalendarToday,
  MenuBook,
  Grade as GradeIcon,
  Star,
  StarBorder,
  Science as ScienceIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import api from '../services/api';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// ── School color palette ──────────────────────────────────────────────────────
const schoolColors = {
  primary:   '#1a237e',
  secondary: '#0d47a1',
  accent:    '#ff6f00',
  gold:      '#ffd700',
  dark:      '#0a0a2a',
  light:     '#e3f2fd',
  white:     '#ffffff',
  textDark:  '#1a1a2e',
  textLight: '#ffffff',
  green:     '#2e7d32',
};

// ── Styled components ─────────────────────────────────────────────────────────
const GlassCard = styled(Card)(({ theme }) => ({
  background: schoolColors.white,
  backdropFilter: 'blur(20px)',
  borderRadius: 24,
  border: `1px solid ${schoolColors.primary}15`,
  boxShadow: `0 8px 32px ${schoolColors.primary}15`,
}));

const SchoolButton = styled(Button)(({ theme, schooltype }) => ({
  borderRadius: 8,
  padding: '12px 28px',
  textTransform: 'none',
  fontWeight: 600,
  background: schooltype === 'primary' 
    ? `linear-gradient(135deg, ${schoolColors.green}, #4caf50)`
    : `linear-gradient(135deg, ${schoolColors.primary}, ${schoolColors.secondary})`,
  color: schoolColors.white,
  '&:hover': {
    transform: 'translateY(-2px)',
  },
  '&.Mui-disabled': {
    background: '#ccc',
    color: '#888',
  },
}));

// ── Component ─────────────────────────────────────────────────────────────────
const ParentResults = () => {
  const [searchReg, setSearchReg] = useState('');
  const [schoolType, setSchoolType] = useState('secondary');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleSearch = async () => {
    if (!searchReg.trim()) return;
    setLoading(true);
    setResults(null);
    try {
      let response;
      if (schoolType === 'secondary') {
        response = await api.get('/public/student-results', {
          params: { registration: searchReg.trim() },
        });
      } else {
        response = await api.get('/primary/results', {
          params: { registration: searchReg.trim() },
        });
      }
      setResults(response.data);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'No results found for this registration number',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A': return 'success';
      case 'B': return 'info';
      case 'C': return 'primary';
      case 'D': return 'warning';
      case 'E': return 'warning';
      case 'F': return 'error';
      default: return 'default';
    }
  };

  const getPerformanceIcon = (percentage) => {
    if (percentage >= 75) return <TrendingUp sx={{ color: 'success.main' }} />;
    if (percentage >= 50) return <TrendingUp sx={{ color: 'warning.main' }} />;
    return <TrendingDown sx={{ color: 'error.main' }} />;
  };

  const handleDownloadPDF = () => {
    if (!results) return;
    const doc = new jsPDF();

    if (schoolType === 'secondary') {
      doc.setFontSize(20);
      doc.setTextColor(26, 35, 126);
      doc.text('Kaozya Secondary School', 105, 20, { align: 'center' });
      doc.setFontSize(16);
      doc.setTextColor(255, 111, 0);
      doc.text('Student Results Slip', 105, 30, { align: 'center' });

      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Name: ${results.student_name}`, 20, 45);
      doc.text(`Registration: ${results.registration_number}`, 20, 52);
      doc.text(`Form: ${results.form_level?.replace('form', 'Form ')}`, 20, 59);
      doc.text(`Academic Year: ${results.academic_year} — ${results.term}`, 20, 66);

      let yPos = 80;
      Object.entries(results.results_by_exam).forEach(([examType, marks]) => {
        doc.setFontSize(14);
        doc.setTextColor(26, 35, 126);
        doc.text(examType, 20, yPos);
        yPos += 10;

        doc.autoTable({
          startY: yPos,
          head: [['Subject', 'Marks', 'Total', 'Percentage', 'Grade']],
          body: marks.map(m => [m.subject, m.marks_obtained, m.total_marks, `${m.percentage}%`, m.grade]),
          theme: 'striped',
          headStyles: { fillColor: [26, 35, 126] },
        });
        yPos = doc.lastAutoTable.finalY + 15;
      });

      doc.setFontSize(14);
      doc.setTextColor(26, 35, 126);
      doc.text('Summary', 20, yPos);
      yPos += 10;
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Total Subjects: ${results.summary.total_subjects}`, 30, yPos);
      doc.text(`Total Marks: ${results.summary.total_marks}/${results.summary.total_possible}`, 30, yPos + 7);
      doc.text(`Average: ${results.summary.average_percentage}%`, 30, yPos + 14);
      doc.text(`Overall Grade: ${results.summary.overall_grade}`, 30, yPos + 21);
    } else {
      // Primary school PDF
      doc.setFontSize(20);
      doc.setTextColor(46, 125, 50);
      doc.text('Kaozya English Medium School', 105, 20, { align: 'center' });
      doc.setFontSize(16);
      doc.setTextColor(255, 111, 0);
      doc.text('Primary School Results Slip', 105, 30, { align: 'center' });

      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Name: ${results.student_name}`, 20, 45);
      doc.text(`Registration: ${results.registration_number}`, 20, 52);
      doc.text(`Class: ${results.class}`, 20, 59);
      doc.text(`Term: ${results.term} | Year: ${results.academic_year}`, 20, 66);

      const tableData = results.results.map(r => [
        r.subject,
        `${r.marks_obtained}/${r.total_marks}`,
        `${r.percentage}%`,
        r.grade
      ]);

      doc.autoTable({
        startY: 80,
        head: [['Subject', 'Marks', 'Percentage', 'Grade']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [46, 125, 50] },
      });

      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(12);
      doc.text(`Average: ${results.summary.average_percentage}%`, 20, finalY);
      doc.text(`Total Subjects: ${results.summary.total_subjects}`, 20, finalY + 7);
    }

    doc.save(`results_${results.registration_number}.pdf`);
  };

  // Render Secondary School Results
  const renderSecondaryResults = () => (
    <>
      {/* Student info card */}
      <GlassCard sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Avatar sx={{ width: 80, height: 80, fontSize: 36, fontWeight: 700, background: `linear-gradient(135deg, ${schoolColors.primary}, ${schoolColors.secondary})` }}>
                  {results.student_name?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: schoolColors.primary }}>
                    {results.student_name}
                  </Typography>
                  <Typography variant="body1" sx={{ color: schoolColors.secondary, fontFamily: 'monospace' }}>
                    {results.registration_number}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <Chip size="small" icon={<School />} label={results.form_level?.replace('form', 'Form ')} sx={{ bgcolor: `${schoolColors.primary}15`, color: schoolColors.primary, fontWeight: 600 }} />
                    <Chip size="small" icon={<CalendarToday />} label={`${results.academic_year} — ${results.term}`} sx={{ bgcolor: `${schoolColors.accent}15`, color: schoolColors.accent, fontWeight: 600 }} />
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                {[
                  { title: 'Download PDF', icon: <Download />, onClick: handleDownloadPDF },
                  { title: 'Print', icon: <Print />, onClick: () => window.print() },
                  { title: 'Share', icon: <Share />, onClick: () => {} },
                ].map(({ title, icon, onClick }) => (
                  <Tooltip title={title} key={title}>
                    <IconButton onClick={onClick} sx={{ color: schoolColors.primary, border: `1px solid ${schoolColors.primary}25`, borderRadius: 2, '&:hover': { bgcolor: `${schoolColors.primary}10`, borderColor: schoolColors.accent, color: schoolColors.accent } }}>
                      {icon}
                    </IconButton>
                  </Tooltip>
                ))}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </GlassCard>

      {/* Summary stat cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { value: results.summary.total_subjects, label: 'Subjects Taken', color: schoolColors.primary, bg: `${schoolColors.primary}12` },
          { value: `${results.summary.average_percentage}%`, label: 'Average Score', color: '#4caf50', bg: '#4caf5012' },
          { value: results.summary.overall_grade, label: 'Overall Grade', color: schoolColors.accent, bg: `${schoolColors.accent}12` },
        ].map(({ value, label, color, bg }) => (
          <Grid item xs={12} md={4} key={label}>
            <Card sx={{ p: 3, textAlign: 'center', bgcolor: bg, borderRadius: 4, border: `1px solid ${color}25`, boxShadow: `0 4px 16px ${color}15` }}>
              <Typography variant="h3" sx={{ color, fontWeight: 700 }}>{value}</Typography>
              <Typography variant="body1" sx={{ color, opacity: 0.75, mt: 0.5 }}>{label}</Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Results by exam type */}
      {Object.entries(results.results_by_exam).map(([examType, marks], index) => (
        <GlassCard key={index} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, pb: 1.5, borderBottom: `2px solid ${schoolColors.primary}20` }}>
              <Box sx={{ width: 4, height: 24, borderRadius: 2, background: `linear-gradient(180deg, ${schoolColors.primary}, ${schoolColors.accent})` }} />
              <GradeIcon sx={{ color: schoolColors.primary }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: schoolColors.primary }}>{examType} Results</Typography>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {['Subject', 'Marks Obtained', 'Total Marks', 'Percentage', 'Grade', 'Performance'].map(col => (
                      <TableCell key={col} align={col === 'Subject' ? 'left' : 'center'} sx={{ bgcolor: schoolColors.primary, color: schoolColors.white, fontWeight: 700, fontSize: 13 }}>
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {marks.map((mark, i) => (
                    <TableRow key={i} hover sx={{ '&:hover': { bgcolor: `${schoolColors.primary}05` } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MenuBook fontSize="small" sx={{ color: schoolColors.secondary }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{mark.subject}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, color: schoolColors.primary }}>{mark.marks_obtained}</TableCell>
                      <TableCell align="center">{mark.total_marks}</TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={`${mark.percentage}%`} sx={{ fontWeight: 600, bgcolor: mark.percentage >= 50 ? '#4caf5018' : '#f4433618', color: mark.percentage >= 50 ? '#4caf50' : '#f44336' }} />
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={mark.grade} color={getGradeColor(mark.grade)} sx={{ fontWeight: 700 }} />
                      </TableCell>
                      <TableCell align="center">{getPerformanceIcon(mark.percentage)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </GlassCard>
      ))}
    </>
  );

  // Render Primary School Results
  const renderPrimaryResults = () => (
    <>
      {/* Student info card */}
      <GlassCard sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Avatar sx={{ width: 80, height: 80, fontSize: 36, fontWeight: 700, background: `linear-gradient(135deg, ${schoolColors.green}, #4caf50)` }}>
                  {results.student_name?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: schoolColors.green }}>
                    {results.student_name}
                  </Typography>
                  <Typography variant="body1" sx={{ color: schoolColors.secondary, fontFamily: 'monospace' }}>
                    {results.registration_number}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <Chip size="small" icon={<School />} label={results.class} sx={{ bgcolor: `${schoolColors.green}15`, color: schoolColors.green, fontWeight: 600 }} />
                    <Chip size="small" icon={<CalendarToday />} label={`${results.term} ${results.academic_year}`} sx={{ bgcolor: `${schoolColors.accent}15`, color: schoolColors.accent, fontWeight: 600 }} />
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Tooltip title="Download PDF">
                  <IconButton onClick={handleDownloadPDF} sx={{ color: schoolColors.green, border: `1px solid ${schoolColors.green}25`, borderRadius: 2, '&:hover': { bgcolor: `${schoolColors.green}10`, borderColor: schoolColors.accent } }}>
                    <Download />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Print">
                  <IconButton onClick={() => window.print()} sx={{ color: schoolColors.green, border: `1px solid ${schoolColors.green}25`, borderRadius: 2 }}>
                    <Print />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </GlassCard>

      {/* Summary stat cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, textAlign: 'center', bgcolor: `${schoolColors.green}12`, borderRadius: 4 }}>
            <Typography variant="h3" sx={{ color: schoolColors.green, fontWeight: 700 }}>{results.summary.total_subjects}</Typography>
            <Typography variant="body1">Subjects Taken</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, textAlign: 'center', bgcolor: `${schoolColors.accent}12`, borderRadius: 4 }}>
            <Typography variant="h3" sx={{ color: schoolColors.accent, fontWeight: 700 }}>{results.summary.average_percentage}%</Typography>
            <Typography variant="body1">Average Score</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, textAlign: 'center', bgcolor: `${schoolColors.gold}12`, borderRadius: 4 }}>
            <Typography variant="h3" sx={{ color: schoolColors.gold, fontWeight: 700 }}>{results.summary.total_marks}/{results.summary.total_possible}</Typography>
            <Typography variant="body1">Total Marks</Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Results Table */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden', mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: schoolColors.green }}>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Subject</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Marks</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Percentage</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Grade</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Performance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.results.map((result, idx) => (
                <TableRow key={idx} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MenuBook fontSize="small" sx={{ color: schoolColors.green }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{result.subject}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{result.marks_obtained}/{result.total_marks}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={`${result.percentage}%`} sx={{ fontWeight: 600, bgcolor: result.percentage >= 50 ? '#4caf5018' : '#f4433618', color: result.percentage >= 50 ? '#4caf50' : '#f44336' }} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={result.grade} color={getGradeColor(result.grade)} sx={{ fontWeight: 700, minWidth: 50 }} />
                  </TableCell>
                  <TableCell align="center">{getPerformanceIcon(result.percentage)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>

      {/* ── Page banner ── */}
      <Box
        sx={{
          mb: 4, p: 3, borderRadius: 3, textAlign: 'center',
          background: `linear-gradient(135deg, ${schoolColors.primary}, ${schoolColors.secondary})`,
          boxShadow: `0 10px 30px ${schoolColors.primary}40`,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700, color: schoolColors.white, mb: 0.5 }}>
          Parent Results Portal
        </Typography>
        <Typography variant="body1" sx={{ color: schoolColors.gold }}>
          View your child's academic performance - Secondary & Primary School
        </Typography>
      </Box>

      {/* ── School Type Selector ── */}
      <GlassCard sx={{ mb: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
            <Button
              variant={schoolType === 'secondary' ? 'contained' : 'outlined'}
              onClick={() => setSchoolType('secondary')}
              startIcon={<School />}
              sx={{
                borderRadius: 3,
                px: 4,
                py: 1,
                bgcolor: schoolType === 'secondary' ? schoolColors.primary : 'transparent',
                borderColor: schoolColors.primary,
                color: schoolType === 'secondary' ? 'white' : schoolColors.primary,
                '&:hover': {
                  bgcolor: schoolType === 'secondary' ? schoolColors.secondary : `${schoolColors.primary}10`,
                },
              }}
            >
              Secondary School
            </Button>
            <Button
              variant={schoolType === 'primary' ? 'contained' : 'outlined'}
              onClick={() => setSchoolType('primary')}
              startIcon={<ScienceIcon />}
              sx={{
                borderRadius: 3,
                px: 4,
                py: 1,
                bgcolor: schoolType === 'primary' ? schoolColors.green : 'transparent',
                borderColor: schoolColors.green,
                color: schoolType === 'primary' ? 'white' : schoolColors.green,
                '&:hover': {
                  bgcolor: schoolType === 'primary' ? '#1b5e20' : `${schoolColors.green}10`,
                },
              }}
            >
              Primary School
            </Button>
          </Box>
          <Typography variant="body2" align="center" color="text.secondary">
            {schoolType === 'secondary' 
              ? 'Enter Secondary School Registration Number (Format: S.XXXXX/NECTA2026)'
              : 'Enter Primary School Registration Number (Format: PRI/2026/XXXX)'}
          </Typography>
        </CardContent>
      </GlassCard>

      {/* ── Search section ── */}
      <GlassCard sx={{ mb: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Search sx={{ color: schoolType === 'primary' ? schoolColors.green : schoolColors.primary }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: schoolType === 'primary' ? schoolColors.green : schoolColors.primary }}>
              Search Student Results
            </Typography>
          </Box>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Student Registration Number"
                placeholder={schoolType === 'secondary' ? "e.g., S.12345/NECTA2026" : "e.g., PRI/2026/0001"}
                value={searchReg}
                onChange={(e) => setSearchReg(e.target.value)}
                onKeyPress={handleKeyPress}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: schoolType === 'primary' ? schoolColors.green : schoolColors.primary }} />,
                }}
                sx={{
                  '& label.Mui-focused': { color: schoolType === 'primary' ? schoolColors.green : schoolColors.primary },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: schoolType === 'primary' ? schoolColors.green : schoolColors.primary },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SchoolButton
                fullWidth
                size="large"
                onClick={handleSearch}
                disabled={loading || !searchReg}
                startIcon={<Search />}
                schooltype={schoolType}
              >
                {loading ? 'Searching...' : 'View Results'}
              </SchoolButton>
            </Grid>
          </Grid>
        </CardContent>
      </GlassCard>

      {/* ── Loading bar ── */}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* ── Results ── */}
      {results && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {schoolType === 'secondary' ? renderSecondaryResults() : renderPrimaryResults()}

          {/* Performance summary - common for both */}
          <GlassCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, pb: 1.5, borderBottom: `2px solid ${schoolColors.primary}20` }}>
                <Box sx={{ width: 4, height: 24, borderRadius: 2, background: `linear-gradient(180deg, ${schoolColors.primary}, ${schoolColors.accent})` }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: schoolColors.primary }}>Performance Summary</Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 1.5, borderRadius: 2, bgcolor: `${schoolColors.primary}06` }}>
                    <Star sx={{ color: schoolColors.gold }} />
                    <Typography variant="body1">
                      <strong>Highest Score:</strong> {schoolType === 'secondary' 
                        ? `${Math.max(...Object.values(results.results_by_exam).flat().map(m => m.percentage))}%`
                        : `${Math.max(...results.results.map(m => m.percentage))}%`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 1.5, borderRadius: 2, bgcolor: `${schoolColors.primary}06` }}>
                    <StarBorder sx={{ color: schoolColors.secondary }} />
                    <Typography variant="body1">
                      <strong>Lowest Score:</strong> {schoolType === 'secondary'
                        ? `${Math.min(...Object.values(results.results_by_exam).flat().map(m => m.percentage))}%`
                        : `${Math.min(...results.results.map(m => m.percentage))}%`}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 1.5, borderRadius: 2, bgcolor: '#4caf5008', border: '1px solid #4caf5020' }}>
                    <CheckCircle sx={{ color: '#4caf50' }} />
                    <Typography variant="body1">
                      <strong>Subjects Passed:</strong>{' '}
                      {schoolType === 'secondary'
                        ? `${Object.values(results.results_by_exam).flat().filter(m => m.percentage >= 50).length} / ${results.summary.total_subjects}`
                        : `${results.results.filter(m => m.percentage >= 50).length} / ${results.summary.total_subjects}`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </GlassCard>
        </motion.div>
      )}

      {/* ── Empty state ── */}
      {!results && !loading && (
        <GlassCard>
          <CardContent sx={{ textAlign: 'center', py: 10 }}>
            <Box
              sx={{
                width: 90, height: 90, borderRadius: '50%', mx: 'auto', mb: 3,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: `linear-gradient(135deg, ${schoolColors.primary}15, ${schoolColors.accent}15)`,
                border: `2px dashed ${schoolColors.primary}30`,
              }}
            >
              <Search sx={{ fontSize: 40, color: schoolColors.primary, opacity: 0.6 }} />
            </Box>
            <Typography variant="h6" sx={{ color: schoolColors.primary, fontWeight: 600, mb: 1 }}>
              Enter a registration number to view results
            </Typography>
            <Typography variant="body2" sx={{ color: schoolColors.secondary, opacity: 0.7 }}>
              Results are available for all enrolled Kaozya Secondary and Primary School students
            </Typography>
          </CardContent>
        </GlassCard>
      )}

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ParentResults;